/**
 * Utility module: env.
 */

import { building } from '$app/environment';

/**
 * Check if the page is being hosted inside the Reuters mobile app.
 *
 * @example
 * ```typescript
 * import { page } from "$app/stores";
 *
 * isReutersApp($page.url);
 * ```
 * @param url URL of current page
 * @returns `true` if in the Reuters app
 */
export const isReutersApp = (url: URL) => {
  if (building) return false;
  return url.searchParams.get('outputType') === 'chromeless';
};

/**
 * Validator escape-hatch: a `window`-level flag the toolkit's
 * `validate-seo` runner sets *before* any page script runs (via
 * Playwright's `addInitScript`). When present, `isReutersDotcom`
 * returns `true` regardless of hostname so the validator can observe
 * `registerPageview` firing on localhost / preview hosts.
 *
 * Why a `window` flag and not a URL query param?  Two reasons:
 *
 *   1. URL params change `link[rel="canonical"]` and `og:url` tags in
 *      the prerendered HTML the validator is supposed to *audit*, so
 *      a query-param approach skews its own measurements.
 *   2. `url.searchParams.get(...)` throws during SvelteKit's prerender
 *      pass ("Cannot access url.searchParams on a page with
 *      prerendering enabled"), and analytics never run server-side
 *      anyway — keeping the flag on `window` keeps the SSR path clean.
 *
 * The global is namespaced with a deliberately awkward, unlikely
 * identifier so it can't be triggered by accident on production. A
 * developer can still set it from devtools (`window.__seoValidator = true`)
 * to verify analytics manually.
 */
declare global {
  interface Window {
    /**
     * Set to `true` by the `validate-seo` toolkit before any page
     * script runs (via Playwright's `addInitScript`). Forces
     * `isReutersDotcom` to return `true` so analytics fire on
     * non-Reuters hosts during validation.
     */
    __seoValidatorOverride?: boolean;
  }
}

/**
 * Check if the page is being hosted on reuters.com.
 *
 * @example
 * ```typescript
 * import { page } from "$app/stores";
 *
 * isReutersDotcom($page.url);
 * ```
 *
 * @param url URL of current page
 * @returns `true` if on reuters.com and not in an iframe
 */
export const isReutersDotcom = (url: URL) => {
  if (typeof window !== 'undefined') {
    if (window.self !== window.top) return false;
    // Validator override — see the `window.__seoValidatorOverride`
    // doc above. Confined to the browser branch because analytics
    // never run on the server, so SSR / prerender shouldn't even
    // reach this check.
    if (window.__seoValidatorOverride === true) return true;
  }
  return /\Wreuters\.com$/.test(url.hostname);
};

/**
 * Check if the page is being hosted on our preview server at graphics.thomsonreuters.com.
 *
 * @example
 * ```typescript
 * import { page } from "$app/stores";
 *
 * isReutersPreview($page.url);
 * ```
 *
 * @param url URL of current page
 * @returns `true` if on graphics.thomsonreuters.com
 */
export const isReutersPreview = (url: URL) => {
  return url.hostname === 'graphics.thomsonreuters.com';
};

/**
 * Check if the page is being hosted in development or on our preview server at graphics.thomsonreuters.com.
 *
 * @example
 * ```typescript
 * import { page } from "$app/stores";
 *
 * isReutersDev($page.url);
 * ```
 *
 * @param url URL of current page
 * @returns `true` if on dev or graphics.thomsonreuters.com
 */
export const isReutersDev = (url: URL) => {
  return url.hostname === 'localhost' || isReutersPreview(url);
};
